import React from 'react';
import { useTheme } from '@mui/material/styles';

/**
 * OTPSkeleton - Skeleton para la página de verificación OTP
 * Estructura: Título (doble línea), Subtítulo, Identifier, 5 digit inputs, Resend link, CTA button
 * Posición centrada verticalmente como StepOTP
 */
const OTPSkeleton: React.FC = () => {
  const theme = useTheme();
  
  const skeletonColors = {
    page: theme.palette.mode === 'light' ? '#ffffff' : '#10141C',
    block: theme.palette.mode === 'light' ? '#e9edf2' : '#2f363e',
    shine: theme.palette.mode === 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.08)'
  };

  const skeletonStyles = `
    .otp-skeleton-page {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-height: 100vh;
      background: ${skeletonColors.page};
      padding: 100px 20px 20px;
    }

    .otp-skeleton-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 520px;
      width: 100%;
      gap: 12px;
    }

    /* Title block - mismo alto que Initial (32px) */
    .otp-skel-title {
      width: 280px;
      height: 32px;
      background: ${skeletonColors.block};
      border-radius: 8px;
      margin-bottom: 8px;
    }

    .otp-skel-subtitle {
      width: 320px;
      height: 16px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .otp-skel-identifier {
      width: 200px;
      height: 18px;
      background: ${skeletonColors.block};
      border-radius: 6px;
      margin-bottom: 16px;
    }

    .otp-skel-digits {
      display: flex;
      gap: 12px;
      margin: 24px 0 16px;
    }

    .otp-skel-digit {
      width: 45px;
      height: 55px;
      background: ${skeletonColors.block};
      border-radius: 8px;
    }

    .otp-skel-resend {
      display: flex;
      gap: 8px;
      margin-bottom: 24px;
    }

    .otp-skel-resend-text {
      width: 140px;
      height: 14px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .otp-skel-resend-link {
      width: 70px;
      height: 14px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .otp-skel-button {
      width: 100%;
      max-width: 400px;
      height: 50px;
      background: ${skeletonColors.block};
      border-radius: 10px;
    }

    .shimmer {
      position: relative;
      overflow: hidden;
    }

    .shimmer::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-150%);
      background: linear-gradient(90deg, transparent 0%, ${skeletonColors.shine} 50%, transparent 100%);
      animation: otpShimmerAnim 1.6s ease-in-out infinite;
    }

    @keyframes otpShimmerAnim {
      0% { transform: translateX(-150%); }
      100% { transform: translateX(150%); }
    }
  `;

  return (
    <div className="otp-skeleton-page">
      <style>{skeletonStyles}</style>

      <div className="otp-skeleton-content">
        {/* Title - doble línea */}
        <div className="otp-skel-title shimmer" />
        <div className="otp-skel-subtitle shimmer" />
        <div className="otp-skel-identifier shimmer" />

        {/* 5 digit inputs */}
        <div className="otp-skel-digits">
          <div className="otp-skel-digit shimmer" />
          <div className="otp-skel-digit shimmer" />
          <div className="otp-skel-digit shimmer" />
          <div className="otp-skel-digit shimmer" />
          <div className="otp-skel-digit shimmer" />
        </div>

        {/* Resend link placeholder */}
        <div className="otp-skel-resend">
          <div className="otp-skel-resend-text shimmer" />
          <div className="otp-skel-resend-link shimmer" />
        </div>

        {/* CTA Button */}
        <div className="otp-skel-button shimmer" />
      </div>
    </div>
  );
};

export default OTPSkeleton;
